import { useEffect, useState } from 'react';
import { Box, Fade, Paper, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

/**
 * Aparição em cascata, lenta e discreta. Sem keyframes próprios — só o Fade do
 * MUI com atraso crescente, para a tela "assentar" em vez de piscar.
 */
export function AoEntrar({ atraso, children }: { atraso: number; children: JSX.Element }): JSX.Element {
  const [visivel, setVisivel] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisivel(true), atraso);
    return () => clearTimeout(t);
  }, [atraso]);
  return (
    <Fade in={visivel} timeout={700}>
      <Box
        sx={{
          transform: visivel ? 'none' : 'translateY(8px)',
          transition: 'transform 700ms cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {children}
      </Box>
    </Fade>
  );
}

/** Bloco de topo das telas de rede. Sem fundo — o acento fica nos ícones e chips. */
export function PainelDestaque({ children }: { children: React.ReactNode }): JSX.Element {
  return <Paper variant="outlined" sx={{ p: { xs: 3, md: 5 } }}>{children}</Paper>;
}

/** Cartão de um ponto explicativo: ícone em caixa de acento + título + texto. */
export function CartaoPonto({
  icon,
  titulo,
  texto,
}: {
  icon: JSX.Element;
  titulo: string;
  texto: string;
}): JSX.Element {
  const tema = useTheme();
  const escuro = tema.palette.mode === 'dark';
  const acento = tema.palette.primary.main;
  return (
    <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box
          sx={{
            display: 'flex',
            p: 1,
            borderRadius: 1.5,
            color: acento,
            bgcolor: alpha(acento, escuro ? 0.18 : 0.09),
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography fontWeight={600} gutterBottom>
            {titulo}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {texto}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

/**
 * Grade responsiva dos cartões: 1 coluna no mobile, 2 no tablet e 4 no desktop
 * largo — sem o passo de 4 os cartões esticam demais agora que a tela ocupa a
 * largura inteira, como as outras do backoffice.
 */
export function Grade({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' },
        gap: { xs: 2, md: 3 },
      }}
    >
      {children}
    </Box>
  );
}
